import {
  ChevronsUpDown,
  LogOut,
  Check,
  Plus,
  Loader2,
} from "lucide-react";
import { useState, type FormEvent } from "react";
import { Avatar, AvatarFallback } from "@/frontend/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/frontend/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/frontend/components/ui/dialog";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Label } from "@/frontend/components/ui/label";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/frontend/components/ui/sidebar";
import { useSession, authClient } from "@/frontend/lib/auth";
import { useNavigate } from "react-router-dom";
import { useOrgStore } from "@/frontend/stores/orgStore";
import { useOrganizations } from "@/frontend/hooks/useOrganizations";
import { switchOrganization } from "@/frontend/lib/switch-organization";
import { useConfirmationDialog } from "@/frontend/hooks/useConfirmationDialog";
import { useCallStore } from "@/frontend/stores/callStore";
import {
  generateOrganizationSlug,
  isQuerygenEmail,
} from "@/shared/organization-creation";
import { toast } from "sonner";
import { accountEmailLabel } from "@/shared/phone-account";

export function NavUser() {
  const { isMobile } = useSidebar();
  const { data: session } = useSession();
  const navigate = useNavigate();
  const { organizations = [], mutate: mutateOrganizations } = useOrganizations();
  const { currentOrgId, setCurrentOrg, clearCurrentOrg } = useOrgStore();
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [organizationName, setOrganizationName] = useState("");
  const [isCreatingOrganization, setIsCreatingOrganization] = useState(false);
  const { requestConfirmation, confirmationDialog } = useConfirmationDialog();

  const user = session?.user;

  if (!user) {
    return null;
  }

  const getInitials = (name: string | undefined | null) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleSignOut = async () => {
    // Clear local state first
    clearCurrentOrg();

    await authClient.signOut({
      fetchOptions: {
        onSuccess: () => {
          navigate("/login");
        },
      },
    });
  };

  const handleOrgChange = async (orgId: string) => {
    // Leaving the workspace hangs up its call
    if (useCallStore.getState().active && !(await requestConfirmation({
      title: "End your call?",
      description: "Switching workspace hangs up the WhatsApp call you're on.",
      confirmLabel: "End call and switch",
      destructive: true,
    }))) return;
    try {
      await switchOrganization({ organizationId: orgId, setCurrentOrg });
      navigate(`/org/${orgId}/app/dashboard`);
    } catch (error) {
      console.error("Error switching organization:", error);
    }
  };

  const handleCreateOrganization = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const name = organizationName.trim();
    if (!name) return;

    setIsCreatingOrganization(true);
    try {
      const result = await authClient.organization.create({
        name,
        slug: generateOrganizationSlug(name),
      });

      if (result.error || !result.data?.id) {
        throw new Error(result.error?.message || "Failed to create organization");
      }

      const organizationId = result.data.id;
      setCreateOrgOpen(false);
      setOrganizationName("");

      try {
        await switchOrganization({ organizationId, setCurrentOrg });
        void mutateOrganizations().catch((error) => {
          console.error("Error refreshing organizations after creation:", error);
        });
        toast.success(`${name} created`);
        navigate(`/org/${organizationId}/app/dashboard`);
      } catch (error) {
        void mutateOrganizations().catch((refreshError) => {
          console.error("Error refreshing organizations after creation:", refreshError);
        });
        toast.error(`${name} was created, but could not be opened. Select it from the organization list.`);
        console.error("Error switching to newly created organization:", error);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to create organization");
    } finally {
      setIsCreatingOrganization(false);
    }
  };

  const canCreateAdditionalOrganization = isQuerygenEmail(user.email);

  return (
    <>
      {confirmationDialog}
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground overflow-hidden"
              >
                <Avatar className="h-8 w-8 rounded-lg shrink-0">
                  <AvatarFallback className="rounded-lg bg-primary text-primary-foreground">
                    {getInitials(user.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight min-w-0 overflow-hidden">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {accountEmailLabel(user.email)}
                  </span>
                </div>
                <ChevronsUpDown className="ml-auto size-4 shrink-0" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm overflow-hidden">
                  <Avatar className="h-8 w-8 rounded-lg shrink-0">
                    <AvatarFallback className="rounded-lg bg-primary text-primary-foreground">
                      {getInitials(user.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight min-w-0 overflow-hidden">
                    <span className="truncate font-semibold">{user.name}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      {accountEmailLabel(user.email)}
                    </span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />

              {/* Organization Selector */}
              <DropdownMenuGroup>
                <DropdownMenuLabel className="text-xs text-muted-foreground font-normal px-2">
                  Organizations
                </DropdownMenuLabel>
                {organizations.map((org) => (
                  <DropdownMenuItem
                    key={org.id}
                    onClick={() => handleOrgChange(org.id)}
                    className="gap-2"
                  >
                    <Avatar className="h-5 w-5">
                      <AvatarFallback className="bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400 text-xs font-medium">
                        {getInitials(org.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="flex-1">{org.name}</span>
                    {currentOrgId === org.id && (
                      <Check className="h-4 w-4 text-green-600" />
                    )}
                  </DropdownMenuItem>
                ))}
                {canCreateAdditionalOrganization && (
                  <DropdownMenuItem
                    onSelect={() => setCreateOrgOpen(true)}
                    className="mt-1 gap-2 border-t pt-2 text-green-700 focus:text-green-700 dark:text-green-400 dark:focus:text-green-400"
                  >
                    <span className="flex h-5 w-5 items-center justify-center rounded-md border border-green-200 bg-green-50 dark:border-green-500/30 dark:bg-green-500/10">
                      <Plus className="h-3.5 w-3.5" />
                    </span>
                    Create organization
                  </DropdownMenuItem>
                )}
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      <Dialog
        open={createOrgOpen}
        onOpenChange={(open) => {
          if (!isCreatingOrganization) setCreateOrgOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <form onSubmit={handleCreateOrganization}>
            <DialogHeader>
              <DialogTitle>Create organization</DialogTitle>
              <DialogDescription>
                Set up a separate workspace with its own members, inbox, and settings.
              </DialogDescription>
            </DialogHeader>
            <div className="py-5">
              <Label htmlFor="organization-name">Organization name</Label>
              <Input
                id="organization-name"
                value={organizationName}
                onChange={(event) => setOrganizationName(event.target.value)}
                placeholder="Acme Operations"
                className="mt-2"
                autoFocus
                disabled={isCreatingOrganization}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateOrgOpen(false)}
                disabled={isCreatingOrganization}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!organizationName.trim() || isCreatingOrganization}
              >
                {isCreatingOrganization && <Loader2 className="animate-spin" />}
                Create organization
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
